import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft, FolderKanban, CheckCircle2, Bot, FileText, Activity,
  GanttChartSquare, ShieldCheck, Lightbulb, ArrowRight, Layers,
} from "lucide-react";
import api from "@/lib/api";
import { PriorityBadge, StatusBadge, Card, CardHeader, Label } from "@/components/common";
import { relativeTime } from "@/lib/format";
import CpmGantt from "@/components/CpmGantt";

const MEMORY = [
  { key: "source", label: "Source Memory", desc: "Indexed files & templates" },
  { key: "project", label: "Project Memory", desc: "Client & job facts" },
  { key: "execution", label: "Execution Memory", desc: "What agents did" },
  { key: "decision", label: "Decision Memory", desc: "Approvals & notes" },
];

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: j, isLoading } = useQuery({
    queryKey: ["job", id],
    queryFn: async () => (await api.get(`/jobs/${id}`)).data,
  });
  if (isLoading || !j) return <div className="text-sm text-ink-muted">Loading job…</div>;

  return (
    <div data-testid="job-detail-page">
      <button onClick={() => navigate("/jobs")} className="flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-4"><ArrowLeft className="w-4 h-4" /> Jobs</button>

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <PriorityBadge value={j.priority} />
            <StatusBadge value={j.status} />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">{j.name}</h1>
          <p className="text-sm text-ink-muted mt-1">{j.client_name} · {j.task_label} · Deliverable: {j.deliverable}</p>
        </div>
        {j.approval && (
          <button onClick={() => navigate(`/approvals/${j.approval.id}`)} data-testid="job-approval-link" className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-dark">
            <ShieldCheck className="w-4 h-4" /> View Approval
          </button>
        )}
      </div>

      {/* Memory layers */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {MEMORY.map((m) => (
          <div key={m.key} className="bg-white border border-border p-3">
            <div className="flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-brand" /><span className="text-xs font-semibold text-ink">{m.label}</span></div>
            <p className="text-[11px] text-ink-faint mt-1">{m.desc}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* CPM */}
          {j.schedule && (
            <Card testId="job-cpm">
              <CardHeader title="Critical Path Schedule" icon={GanttChartSquare} action={<button onClick={() => navigate("/schedule")} className="text-[11px] uppercase tracking-wider text-brand font-semibold">Open Schedule →</button>} />
              <div className="p-4"><CpmGantt schedule={j.schedule} /></div>
            </Card>
          )}

          {/* Task tree */}
          <Card>
            <CardHeader title="Task Tree" icon={CheckCircle2} action={<span className="text-[11px] font-mono text-ink-faint">{j.tasks.length} tasks</span>} />
            <div className="divide-y divide-border">
              {j.tasks.map((t) => (
                <div key={t.id} className="flex items-center gap-3 px-4 py-2.5">
                  <CheckCircle2 className={`w-4 h-4 shrink-0 ${t.status === "done" ? "text-st-success" : "text-ink-faint"}`} />
                  <span className="text-sm text-ink flex-1">{t.name}</span>
                  <span className="text-[11px] text-ink-faint font-mono">{t.assigned_agent}</span>
                  <StatusBadge value={t.status} />
                </div>
              ))}
            </div>
          </Card>

          {/* Agent runs */}
          <Card>
            <CardHeader title="Agent Execution Log" icon={Bot} />
            <div className="divide-y divide-border">
              {j.agent_runs.map((r) => (
                <div key={r.id} className="flex items-start gap-3 px-4 py-2.5">
                  <Bot className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink">{r.agent_name}</span>
                      <span className="text-[10px] uppercase tracking-wider text-st-success font-semibold">{r.status}</span>
                    </div>
                    <p className="text-xs text-ink-muted mt-0.5">{r.summary}</p>
                    {r.files_used?.length > 0 && <div className="text-[10px] font-mono text-ink-faint mt-1">↳ {r.files_used.join(", ")}</div>}
                  </div>
                  <span className="font-mono text-[10px] text-ink-faint">{relativeTime(r.started_at)}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-4">
          {/* Recommendations */}
          {j.recommendations?.length > 0 && (
            <div className="bg-brand/5 border border-brand p-4" data-testid="job-recommendations">
              <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-brand" /><h3 className="font-heading text-sm font-semibold uppercase tracking-tight text-ink">Strategy / Next Steps</h3></div>
              <ul className="space-y-2">
                {j.recommendations.map((r, i) => <li key={i} className="flex items-start gap-2 text-sm text-ink"><ArrowRight className="w-3.5 h-3.5 mt-1 text-brand shrink-0" /> {r}</li>)}
              </ul>
            </div>
          )}

          {/* Files */}
          <Card>
            <CardHeader title="Source Files" icon={FileText} />
            <div className="divide-y divide-border">
              {j.files.map((f) => (
                <div key={f.id} className="flex items-center gap-2 px-4 py-2.5">
                  <FileText className="w-3.5 h-3.5 text-ink-faint shrink-0" />
                  <span className="text-xs font-mono text-ink-muted truncate">{f.name}</span>
                </div>
              ))}
              {j.files.length === 0 && <div className="px-4 py-4 text-sm text-ink-faint">No files linked.</div>}
            </div>
          </Card>

          {/* Timeline */}
          <Card>
            <CardHeader title="Timeline" icon={Activity} />
            <div className="divide-y divide-border max-h-72 overflow-y-auto">
              {j.timeline.map((t) => (
                <div key={t.id} className="px-4 py-2.5">
                  <div className="text-sm text-ink">{t.summary}</div>
                  <div className="flex items-center gap-2 mt-0.5"><span className="text-[11px] font-semibold text-ink-muted">{t.actor}</span><span className="font-mono text-[10px] text-ink-faint">{relativeTime(t.created_at)}</span></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
